import { useState, useEffect, useRef, useCallback } from 'react'
import { io, Socket } from 'socket.io-client'
import type {
  LiveSession,
  LeaderboardState,
  OvertakeEvent,
  ComebackEvent,
  ShoutoutEvent,
  SessionStatePayload,
  QrRefreshEvent,
  RosterEntry,
  ScreenProgram,
  IntervalTimerEvent,
  IntervalTimerState,
} from '@gymboard/types'
import {
  getScreenToken,
  forgetPairing,
  setLastSessionState,
  getLastSessionState,
  setLastEndedSession,
  getLastEndedSession,
  clearLastEndedSession,
} from '../lib/storage'
import { screenApi } from '../lib/api'
import { isStaleLeaderboardFrame, decodeTenantIdFromScreenToken } from '../lib/session-frames'

const WS_URL =
  (import.meta.env.VITE_WS_URL as string | undefined) ?? 'http://localhost:4000'

const EVENT_DISMISS_MS = 5000

/**
 * Minimum gap between full-screen overtake takeovers.
 *
 * The server emits an overtake event on every leaderboard recalc that
 * reshuffles ranks — and recalc runs per metric batch (~every 2-3s per
 * athlete). In a busy class, ranks churn constantly, so without a
 * cooldown the cinematic overlay strobes non-stop: each new event
 * remounts the card (new key) while the old one is still exiting,
 * stacking multiple full-screen layers (observed 6 concurrent in
 * testing) and burying the leaderboard underneath.
 *
 * The overlay is meant to be a *moment*, not a status display. Routine
 * churn is already visualised in-list (rank-move animation, delta
 * chips, duel beams, top ticker). So: after one takeover shows, further
 * overtake events are dropped — not queued — until the cooldown lapses.
 * Dropping (vs queueing) is deliberate: a queued overtake from 20s ago
 * describes a rank order that no longer exists.
 */
const OVERTAKE_COOLDOWN_MS = 12_000

export type SessionStatus = 'PRESTART' | 'LIVE' | 'PAUSED' | 'ENDED'

export interface ScreenSessionState {
  sessionId: string | null
  session: LiveSession | null
  sessionStatus: SessionStatus | null
  leaderboard: LeaderboardState | null
  roster: RosterEntry[]
  qrToken: string | null
  qrSignature: string | null
  qrExpiresAt: number | null
  overtakeEvent: OvertakeEvent | null
  comebackEvent: ComebackEvent | null
  shoutoutEvent: ShoutoutEvent | null
  isConnected: boolean
  isReconnecting: boolean
  elapsedMs: number
  /** Next session waiting in PRESTART (drives split-screen when current is ENDED) */
  upcomingSession: LiveSession | null
  /** QR credentials for the upcoming PRESTART session (for split-screen check-in) */
  upcomingQrToken: string | null
  upcomingQrSignature: string | null
  upcomingQrExpiresAt: number | null
  /** Roster for the upcoming PRESTART session */
  upcomingRoster: RosterEntry[]
  /**
   * Frozen state from the session that just ended.
   * Drives the 5-minute "handoff split" where the new class is LIVE on the
   * right and the previous class's final leaderboard is shown on the left.
   */
  previousSession: LiveSession | null
  previousLeaderboard: LeaderboardState | null
  /** Timestamp (ms) when the previous session ended — used to limit the handoff window. */
  previousEndedAt: number | null
  /**
   * Server-declared screen program — the authoritative phase for the room.
   * When non-null, App.tsx switches on program.phase instead of inferring
   * the screen from status + client timers. Null until the first fetch /
   * socket event resolves, during which App.tsx falls back to the legacy
   * inference so a cold TV still shows something.
   */
  program: ScreenProgram | null
  /** Coach-started interval timer for the main session (null = none). */
  timer: IntervalTimerState | null
  /** serverNow − Date.now() from the last timer sync. */
  clockOffsetMs: number
}

const DEFAULT_STATE: ScreenSessionState = {
  timer: null,
  clockOffsetMs: 0,
  sessionId: null,
  session: null,
  sessionStatus: null,
  leaderboard: null,
  roster: [],
  qrToken: null,
  qrSignature: null,
  qrExpiresAt: null,
  overtakeEvent: null,
  comebackEvent: null,
  shoutoutEvent: null,
  isConnected: false,
  isReconnecting: false,
  elapsedMs: 0,
  upcomingSession: null,
  upcomingQrToken: null,
  upcomingQrSignature: null,
  upcomingQrExpiresAt: null,
  upcomingRoster: [],
  previousSession: null,
  previousLeaderboard: null,
  previousEndedAt: null,
  program: null,
}

/** Recovery window — must match HANDOFF_WINDOW_MINS in App.tsx (3 min) */
const REFRESH_RECOVERY_WINDOW_MS = 3 * 60_000

export function useScreenSession(roomId: string | null): ScreenSessionState {
  const [state, setState] = useState<ScreenSessionState>(() => {
    const initial: ScreenSessionState = { ...DEFAULT_STATE }

    // ── 1. Recover the main session (LIVE/PAUSED/PRESTART) ─────────────────
    try {
      const raw = getLastSessionState()
      if (raw) {
        const recovered = JSON.parse(raw) as Partial<ScreenSessionState>
        if (recovered.sessionStatus && recovered.sessionStatus !== 'ENDED') {
          initial.sessionId = recovered.sessionId ?? null
          initial.session = recovered.session ?? null
          initial.sessionStatus = recovered.sessionStatus ?? null
          initial.leaderboard = recovered.leaderboard ?? null
          // Restore roster + QR so PreClassScreen / LiveLeaderboardScreen don't
          // flicker through an empty state while the connect snapshot is fetching.
          initial.roster = recovered.roster ?? []
          // QR credentials: only restore if not yet expired.
          if (recovered.qrExpiresAt && recovered.qrExpiresAt > Date.now()) {
            initial.qrToken = recovered.qrToken ?? null
            initial.qrSignature = recovered.qrSignature ?? null
            initial.qrExpiresAt = recovered.qrExpiresAt
          }
        }
      }
    } catch {
      // ignore parse errors
    }

    // ── 2. Recover previous session for handoff split refresh ───────────────
    // If class A ended recently (< 5 min ago) for this room, pre-populate the
    // previous* fields so the handoff split (case 4b) still renders after a
    // browser refresh during the 5-minute celebration window.
    try {
      const saved = getLastEndedSession()
      if (
        saved &&
        saved.roomId === roomId &&
        Date.now() - saved.endedAt < REFRESH_RECOVERY_WINDOW_MS
      ) {
        initial.previousSession = JSON.parse(saved.sessionJson)
        initial.previousLeaderboard = saved.leaderboardJson
          ? JSON.parse(saved.leaderboardJson)
          : null
        initial.previousEndedAt = saved.endedAt
      }
    } catch {
      // ignore parse errors
    }

    return initial
  })

  const socketRef = useRef<Socket | null>(null)
  /**
   * Set of sessionIds we've sent `session:join:broadcast` for on the current
   * socket connection. Used to:
   *   1. Avoid double-subscribing when the sync effect re-runs.
   *   2. Tell the cosmetic-event handlers (leaderboard:update, rank:overtake,
   *      comeback:detected) to PREFER the delayed broadcast copy over the
   *      real-time room-channel duplicate — when a sessionId is in this set,
   *      any envelope with a `roomId` tag (room-channel emit) is dropped so
   *      the screen renders at broadcast-delay parity with the public stream.
   * Cleared on disconnect and re-populated on reconnect.
   */
  const broadcastJoinedRef = useRef<Set<string>>(new Set())
  /** Cached tenantId from the screen JWT; needed for session:join:broadcast payload. */
  const tenantIdRef = useRef<string | null>(null)
  const elapsedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const overtakeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Timestamp of the last full-screen overtake takeover — drives the
  // OVERTAKE_COOLDOWN_MS gate in setOvertake. Initialized to 0 so the
  // very first overtake of a session always shows.
  const lastOvertakeShownAtRef = useRef(0)
  const lastComebackShownAtRef = useRef(0)
  const comebackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const shoutoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Timer that re-fetches the screen program at `revalidateAt` — the one
  // clock-based transition the server can't push (handoff expiry, upcoming
  // crossing the split threshold).
  const programRevalidateRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const stateRef = useRef(state)
  stateRef.current = state
  // Tracks the "virtual start" of the timer: Date.now() - elapsedAtThatPoint.
  // We use this ref instead of parsing session.startedAt (which can be null or
  // a Date/string depending on how the session arrived from the server).
  const sessionStartMsRef = useRef<number | null>(null)

  // ─── Stale upcoming cleanup ──────────────────────────────────────────────────
  //
  // If the server fails to auto-start the scheduled session (or its session:started
  // event is lost), `upcomingSession` would sit in state forever with a past
  // scheduledAt.  Clear it once scheduledAt + generous grace has passed so the
  // TV doesn't keep a ghost upcoming panel around.
  useEffect(() => {
    const scheduledAtStr = (state.upcomingSession?.classInstance as { scheduledAt?: string } | undefined)?.scheduledAt
    if (!scheduledAtStr) return
    const targetMs = new Date(scheduledAtStr).getTime()
    if (isNaN(targetMs)) return

    // 2-minute grace beyond scheduledAt before we consider it dead.
    const expireAt = targetMs + 2 * 60_000
    const now = Date.now()
    if (expireAt <= now) {
      // Already stale — clear immediately.
      setState((prev) => ({
        ...prev,
        upcomingSession: null,
        upcomingQrToken: null,
        upcomingQrSignature: null,
        upcomingQrExpiresAt: null,
        upcomingRoster: [],
      }))
      return
    }
    // Use a setInterval that self-polls every 30 s rather than a single
    // setTimeout — so that a tab-throttled TV eventually clears the ghost
    // upcoming on the next wake, even if the exact fire-time was missed.
    const tick = () => {
      setState((prev) => {
        if (!prev.upcomingSession) return prev
        const s = (prev.upcomingSession.classInstance as { scheduledAt?: string } | undefined)?.scheduledAt
        if (!s) return prev
        if (new Date(s).getTime() + 2 * 60_000 > Date.now()) return prev
        return {
          ...prev,
          upcomingSession: null,
          upcomingQrToken: null,
          upcomingQrSignature: null,
          upcomingQrExpiresAt: null,
          upcomingRoster: [],
        }
      })
    }
    // First tick at exact expiry, then poll every 30 s as a safety net.
    const firstDelay = Math.max(0, expireAt - now)
    const first = setTimeout(() => {
      tick()
      // setInterval variable captured by the returned cleanup below.
    }, firstDelay)
    const repeat = setInterval(tick, 30_000)
    return () => {
      clearTimeout(first)
      clearInterval(repeat)
    }
  }, [state.upcomingSession])

  // Persist state to localStorage whenever key fields change.
  //
  // THROTTLED (not debounced): guarantees a write within 1 s of any change,
  // even during a continuous stream of updates (30 athletes pushing metrics at
  // 1 Hz would starve a pure debounce).  At most one write per 1 s to avoid
  // jank from repeated JSON.stringify + setItem.
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastPersistRef = useRef<number>(0)
  const flushPersist = useCallback(() => {
    const s = stateRef.current
    if (!s.sessionStatus) return
    setLastSessionState({
      sessionId: s.sessionId,
      session: s.session,
      sessionStatus: s.sessionStatus,
      leaderboard: s.leaderboard,
      roster: s.roster,
      qrToken: s.qrToken,
      qrSignature: s.qrSignature,
      qrExpiresAt: s.qrExpiresAt,
    })
    lastPersistRef.current = Date.now()
    persistTimerRef.current = null
  }, [])
  useEffect(() => {
    if (!state.sessionStatus) return
    const now = Date.now()
    const sinceLast = now - lastPersistRef.current
    if (sinceLast >= 1000) {
      // Trailing edge of a quiet period OR first write — fire immediately.
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current)
        persistTimerRef.current = null
      }
      flushPersist()
    } else if (!persistTimerRef.current) {
      // Inside the throttle window — schedule one trailing write at the edge.
      // Subsequent updates before the timer fires are no-ops (timer stays
      // scheduled) so we always write the LATEST state at flush time
      // (flushPersist reads stateRef.current).
      persistTimerRef.current = setTimeout(flushPersist, 1000 - sinceLast)
    }
  }, [
    state.sessionStatus,
    state.sessionId,
    state.session,
    state.leaderboard,
    state.roster,
    state.qrToken,
    state.qrSignature,
    state.qrExpiresAt,
    flushPersist,
  ])

  // Flush the throttled persist on unmount so the most recent state isn't lost
  // when the TV app is closed or the tab is navigated away.
  //
  // NOTE: with the throttle pattern, a null persistTimerRef does NOT imply
  // "nothing pending" — the last state change may have fallen on the eager
  // write path, leaving subsequent changes un-persisted if no trailing timer
  // was scheduled.  So we unconditionally flush on unmount.
  useEffect(() => {
    return () => {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current)
        persistTimerRef.current = null
      }
      flushPersist()
    }
  }, [flushPersist])

  // ─── ENDED-state safety-net ──────────────────────────────────────────────
  //
  // App.tsx renders `SplitScreen` (case 5) for `ENDED + upcomingSession` and
  // `EndedScreen` (case 6) for `ENDED, no upcoming`. Case 6 has its own 60-s
  // auto-dismiss, but case 5 has no fallback — if the upcoming class never
  // transitions to LIVE (Bull job stuck, coach cancels, server-side glitch),
  // the TV gets stuck on the post-class split screen indefinitely.
  //
  // This timer fires `ENDED_FALLBACK_MS` after the session enters ENDED and
  // hard-resets the displayed state if we're still in ENDED at that point.
  // Clearing `sessionStatus` to null drops the TV through App.tsx's case 2b
  // ("no active session → ambient"), so the screen reliably returns to the
  // ambient/idle state regardless of which ENDED sub-case we were in.
  //
  // 5 minutes is long enough that legitimate handoffs (3-min handoff window
  // + a couple minutes' coach-tool slack) finish before we kick in, and
  // short enough that a stuck TV is back to ambient within a reasonable
  // interval.
  const ENDED_FALLBACK_MS = 5 * 60_000
  useEffect(() => {
    if (state.sessionStatus !== 'ENDED') return
    const fallback = setTimeout(() => {
      // Only reset if we're still in ENDED — a session:started for an
      // upcoming class that fired in the meantime would have flipped us
      // to LIVE/PAUSED, and we don't want to clobber that.
      if (stateRef.current.sessionStatus !== 'ENDED') return
      console.info('[TV] ENDED state fallback fired — resetting to ambient')
      // Wipe the persisted handoff state too, otherwise a refresh during
      // the ambient-return window could resurrect previousSession and
      // briefly flash the celebration UI before clearing again.
      try { clearLastEndedSession() } catch { /* storage unavailable */ }
      // Reset to ambient. We keep `roomId` / pairing implicit in the hook —
      // only session-derived state needs clearing.
      setState((prev) => ({
        ...prev,
        sessionId: null,
        session: null,
        sessionStatus: null,
        leaderboard: null,
        roster: [],
        qrToken: null,
        qrSignature: null,
        qrExpiresAt: null,
        elapsedMs: 0,
        upcomingSession: null,
        upcomingQrToken: null,
        upcomingQrSignature: null,
        upcomingQrExpiresAt: null,
        upcomingRoster: [],
        previousSession: null,
        previousLeaderboard: null,
        previousEndedAt: null,
      }))
    }, ENDED_FALLBACK_MS)
    return () => clearTimeout(fallback)
  }, [state.sessionStatus])

  // ─── Elapsed timer ───────────────────────────────────────────────────────────
  //
  // We track elapsed time via a local ref (`sessionStartMsRef`) rather than
  // parsing `session.startedAt` from the server — this avoids issues with Date
  // serialisation formats (Date vs ISO string) and server/client clock skew.
  //
  // `sessionStartMsRef.current = Date.now() - baseElapsedMs` is set whenever
  // we know the authoritative elapsed time (session:started, snapshot fetch,
  // session:resumed).  The 1-second interval then reads
  //   elapsed = Date.now() - sessionStartMsRef.current
  // which is always correct without touching session.startedAt.

  const startElapsedTimer = useCallback((baseElapsedMs: number) => {
    if (elapsedIntervalRef.current) clearInterval(elapsedIntervalRef.current)
    sessionStartMsRef.current = Date.now() - baseElapsedMs
    // Immediately apply the base so the display doesn't wait a full second
    setState((prev) => ({ ...prev, elapsedMs: baseElapsedMs }))
    elapsedIntervalRef.current = setInterval(() => {
      const startMs = sessionStartMsRef.current
      if (startMs === null) return
      const elapsed = Math.max(0, Date.now() - startMs)
      setState((prev) => {
        if (prev.sessionStatus !== 'LIVE') return prev
        return { ...prev, elapsedMs: elapsed }
      })
    }, 1000)
  }, [])

  const stopElapsedTimer = useCallback(() => {
    if (elapsedIntervalRef.current) {
      clearInterval(elapsedIntervalRef.current)
      elapsedIntervalRef.current = null
    }
  }, [])

  // ─── Screen program ──────────────────────────────────────────────────────────
  // Apply a program frame: store it and (re)arm the revalidate timer for
  // its clock-based boundary. The program is a small decision object; the
  // heavy session/leaderboard DATA still flows through the existing
  // snapshot/socket paths keyed by session id.
  const applyProgram = useCallback((program: ScreenProgram) => {
    setState((prev) => ({ ...prev, program }))
    if (programRevalidateRef.current) {
      clearTimeout(programRevalidateRef.current)
      programRevalidateRef.current = null
    }
    if (program.revalidateAt != null) {
      // Clamp: never schedule in the past (a boundary that already passed
      // fetches immediately on the next tick), cap at a sane ceiling so a
      // far-future boundary doesn't hold a multi-minute timer that a
      // lifecycle event would supersede anyway.
      const delay = Math.min(
        Math.max(program.revalidateAt - Date.now(), 250),
        10 * 60_000,
      )
      programRevalidateRef.current = setTimeout(() => {
        void fetchProgram()
      }, delay)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchProgram = useCallback(async () => {
    if (!roomId) return
    try {
      const res = await screenApi.getScreenProgram(roomId)
      const program = (res.data as { data?: ScreenProgram })?.data ?? res.data
      if (program && (program as ScreenProgram).phase) {
        applyProgram(program as ScreenProgram)
      }
    } catch (err) {
      console.error('[useScreenSession] program fetch failed', err)
    }
  }, [roomId, applyProgram])

  // ─── Snapshot fetch ──────────────────────────────────────────────────────────

  const fetchSnapshot = useCallback(async (sessionId: string) => {
    try {
      const res = await screenApi.getSnapshot(sessionId)
      const payload = res.data as SessionStatePayload & {
        session: LiveSession
        leaderboard: LeaderboardState
        roster?: RosterEntry[]
        currentQr?: { token: string; signature: string; expiresAt: number }
        timer?: IntervalTimerState | null
        serverNow?: number
      }
      const baseMs = payload.elapsedMs ?? 0
      // Guard: if we've already marked this session as ENDED locally, don't let
      // a stale Redis snapshot resurrect it to LIVE/PAUSED.  The ENDED event is
      // authoritative once we've processed it.
      if (
        stateRef.current.sessionId === sessionId &&
        stateRef.current.sessionStatus === 'ENDED' &&
        (payload.session.status as string) !== 'ENDED'
      ) {
        console.info('[TV] fetchSnapshot: ignoring non-ENDED snapshot for locally-ENDED session', sessionId)
        return
      }
      setState((prev) => ({
        ...prev,
        sessionId,
        session: payload.session,
        sessionStatus: payload.session.status as SessionStatus,
        // Prefer a non-null leaderboard from snapshot; if snapshot has no data
        // yet (e.g., called right after session:started), keep the existing state
        // so a concurrent leaderboard:update event is not wiped out.
        leaderboard: payload.leaderboard ?? prev.leaderboard,
        // Fallback to existing roster if snapshot omits it — avoids a transient
        // blank roster when the server returns a minimal payload mid-LIVE.
        roster: payload.roster ?? prev.roster,
        elapsedMs: baseMs,
        timer: payload.timer ?? null,
        clockOffsetMs: payload.serverNow ? payload.serverNow - Date.now() : prev.clockOffsetMs,
        // Restore QR from snapshot only if it's valid AND newer than whatever
        // we already have.  This avoids clobbering a fresh qr:refresh that
        // arrived between the HTTP request leaving and returning.
        ...(payload.currentQr &&
          payload.currentQr.expiresAt > Date.now() &&
          payload.currentQr.expiresAt > (prev.qrExpiresAt ?? 0)
          ? {
              qrToken: payload.currentQr.token,
              qrSignature: payload.currentQr.signature,
              qrExpiresAt: payload.currentQr.expiresAt,
            }
          : {}),
      }))
      // Only run the timer if the session is LIVE — server gives us the
      // authoritative elapsed time so we start the ref from there.
      if ((payload.session.status as string) === 'LIVE') {
        startElapsedTimer(baseMs)
      } else {
        stopElapsedTimer()
      }
    } catch (err) {
      console.error('[useScreenSession] snapshot fetch failed', err)
    }
  }, [startElapsedTimer, stopElapsedTimer])

  /** Fetch QR + roster for the upcoming PRESTART session. */
  const fetchUpcomingSnapshot = useCallback(async (sessionId: string, attempt = 0) => {
    try {
      const res = await screenApi.getSnapshot(sessionId)
      const payload = res.data as {
        session: LiveSession
        roster?: RosterEntry[]
        currentQr?: { token: string; signature: string; expiresAt: number }
      }
      const hasFreshQr =
        !!payload.currentQr && payload.currentQr.expiresAt > Date.now()
      setState((prev) => ({
        ...prev,
        upcomingRoster: payload.roster ?? [],
        ...(hasFreshQr &&
          payload.currentQr!.expiresAt > (prev.upcomingQrExpiresAt ?? 0)
          ? {
              upcomingQrToken: payload.currentQr!.token,
              upcomingQrSignature: payload.currentQr!.signature,
              upcomingQrExpiresAt: payload.currentQr!.expiresAt,
            }
          : {}),
      }))
      // Race: when a PRESTART session is freshly created the server fires
      // `_generateAndEmitQr` asynchronously.  The first `fetchUpcomingSnapshot`
      // (triggered by `session:upcoming`) can arrive before Redis has a QR.
      // Retry briefly so the upcoming split-screen panel isn't stuck on
      // "Refreshing…" waiting for the 25-second rotation.
      if (!hasFreshQr && attempt < 3) {
        setTimeout(() => void fetchUpcomingSnapshot(sessionId, attempt + 1), 700)
      }
    } catch (err) {
      console.error('[useScreenSession] fetchUpcomingSnapshot failed', err)
    }
  }, [])

  /** Poll for the next PRESTART session (different from the current main session). */
  const lastNextUpAtRef = useRef<number>(0)
  const fetchNextUp = useCallback(async () => {
    if (!roomId) return
    // Dedupe: if we've just fetched within the last 500 ms (e.g., both the
    // connect handler AND session:state fired in the same tick), skip.
    const now = Date.now()
    if (now - lastNextUpAtRef.current < 500) return
    lastNextUpAtRef.current = now
    try {
      const res = await screenApi.getNextUpSession(roomId)
      const upcoming = res.data as LiveSession | null
      if (upcoming) {
        // Guard: never promote the current main session to "upcoming".  Also
        // only fetch the upcoming snapshot when we actually accept the session —
        // otherwise QR/roster for a non-upcoming session would leak into state.
        let accepted = false
        setState((prev) => {
          if (prev.sessionId === upcoming.id) return prev
          // Skip no-op: same upcoming already loaded — avoids an extra
          // fetchUpcomingSnapshot round-trip on repeat polls.
          if (prev.upcomingSession?.id === upcoming.id) return prev
          accepted = true
          return { ...prev, upcomingSession: upcoming }
        })
        if (accepted) void fetchUpcomingSnapshot(upcoming.id)
      }
    } catch (err) {
      console.error('[useScreenSession] fetchNextUp failed', err)
    }
  }, [roomId, fetchUpcomingSnapshot])

  // ─── Safety-net polls ───────────────────────────────────────────────────────
  //
  // Two failure modes the socket-only design can't self-correct from:
  //
  //   1.  A `session:upcoming` event emitted while the TV was momentarily
  //       disconnected (reconnect gap, backgrounded tab, wifi blip) is lost
  //       forever — the server emits once when the PRESTART is created.
  //       Without a fallback, `upcomingSession` stays null and the 5-minute
  //       split-screen handoff never appears.
  //
  //   2.  The server's Bull auto-start job could silently fail (bad coach
  //       membership, Redis blip, etc.), leaving the session in PRESTART
  //       past its scheduledAt.  The TV would sit on PreClassScreen forever.
  //
  // These two polls run unconditionally on the TV and are cheap (one HTTP
  // request per minute each).  They never overwrite fresher WebSocket state —
  // fetchNextUp's no-op guard and fetchSnapshot's ENDED guard handle that.

  // Poll for an upcoming next class every 60 s while a session is active.
  useEffect(() => {
    if (!roomId) return
    if (!state.sessionStatus) return
    const id = setInterval(() => {
      void fetchNextUp()
    }, 60_000)
    return () => clearInterval(id)
  }, [roomId, state.sessionStatus, fetchNextUp])

  // If we're sitting in PRESTART past the scheduled start time, the auto-
  // start event may have been missed (or Bull is a second or two late).
  // Poll the snapshot aggressively so the TV can self-recover to LIVE
  // within a few seconds of the server transitioning.
  // Depend only on the scheduledAt *string* (primitive) and status — NOT the
  // whole `state.session` object.  `fetchSnapshot` replaces `state.session`
  // with a fresh reference every tick, so depending on it would re-create the
  // interval on every snapshot response, and the leading `tick()` call inside
  // the effect would fire another snapshot synchronously — a tight loop that
  // hammered the API at hundreds of req/sec on slower machines.
  const prestartScheduledAt =
    state.sessionStatus === 'PRESTART'
      ? ((state.session?.classInstance as { scheduledAt?: string } | undefined)?.scheduledAt ?? null)
      : null
  useEffect(() => {
    if (!prestartScheduledAt) return
    const scheduledMs = new Date(prestartScheduledAt).getTime()
    if (isNaN(scheduledMs)) return

    const tick = () => {
      // Start polling 2 seconds before the scheduled time so we're ready the
      // instant Bull flips the status — Bull's own clock, the DB commit, and
      // the WebSocket emit can easily add up to several seconds of delay.
      if (Date.now() < scheduledMs - 2_000) return
      const sid = stateRef.current.sessionId
      if (sid) void fetchSnapshot(sid)
    }
    tick()
    // 3-second interval — trades a small amount of extra traffic for a
    // near-instantaneous user-visible transition.
    const id = setInterval(tick, 3_000)
    return () => clearInterval(id)
  }, [prestartScheduledAt, fetchSnapshot])

  // Same idea for the UPCOMING session while we're in the 5-min-before split
  // screen: as soon as the upcoming's scheduledAt passes, poll its snapshot
  // every 3 s.  When the server flips it to LIVE we detect it here and
  // synthesise a session:started-equivalent transition — swapping the current
  // LIVE session out as `previousSession` (for the 3-min handoff split) and
  // promoting the upcoming to main with a fresh 0-elapsed timer.  This is the
  // fix for "class B countdown hit 0 but screen didn't switch to LIVE and
  // class A's timer just kept running".
  useEffect(() => {
    if (!state.upcomingSession) return
    const scheduledAtStr =
      (state.upcomingSession.classInstance as { scheduledAt?: string } | undefined)?.scheduledAt
    if (!scheduledAtStr) return
    const scheduledMs = new Date(scheduledAtStr).getTime()
    if (isNaN(scheduledMs)) return

    const upcomingId = state.upcomingSession.id

    const tick = async () => {
      if (Date.now() < scheduledMs - 2_000) return
      try {
        const res = await screenApi.getSnapshot(upcomingId)
        const payload = res.data as SessionStatePayload & {
          session: LiveSession
          leaderboard: LeaderboardState
          roster?: RosterEntry[]
          currentQr?: { token: string; signature: string; expiresAt: number }
        }
        const status = payload.session.status as string
        if (status !== 'LIVE' && status !== 'PAUSED') return
        // Upcoming has gone LIVE on the server — promote it to main.
        const prev = stateRef.current
        // Only move if this hasn't already happened (idempotency under multiple firings).
        if (prev.sessionId === upcomingId) return

        // Freeze the current LIVE session as `previous*` for the handoff split,
        // and persist so a refresh during the 3-minute window still recovers it.
        const outgoing = prev.session
        const outgoingLb = prev.leaderboard
        if (outgoing && roomId && outgoing.roomId === roomId) {
          setLastEndedSession({
            sessionJson: JSON.stringify(outgoing),
            leaderboardJson: outgoingLb ? JSON.stringify(outgoingLb) : null,
            endedAt: Date.now(),
            roomId,
          })
        }

        const baseMs = payload.elapsedMs ?? 0
        setState((p) => ({
          ...p,
          sessionId: upcomingId,
          session: payload.session,
          sessionStatus: status as SessionStatus,
          leaderboard: payload.leaderboard ?? null,
          roster: payload.roster ?? [],
          elapsedMs: baseMs,
          // Clear upcoming slots — this session is now main.
          upcomingSession: null,
          upcomingQrToken: null,
          upcomingQrSignature: null,
          upcomingQrExpiresAt: null,
          upcomingRoster: [],
          // Promote outgoing to previous for the handoff split.
          previousSession: outgoing,
          previousLeaderboard: outgoingLb,
          previousEndedAt: Date.now(),
          // Restore QR for the new main.
          ...(payload.currentQr &&
            payload.currentQr.expiresAt > Date.now()
            ? {
                qrToken: payload.currentQr.token,
                qrSignature: payload.currentQr.signature,
                qrExpiresAt: payload.currentQr.expiresAt,
              }
            : {}),
        }))
        startElapsedTimer(baseMs)
        console.info('[TV] upcoming→LIVE safety-net promoted', upcomingId)
      } catch (err) {
        console.error('[TV] upcoming→LIVE poll failed', err)
      }
    }
    void tick()
    const id = setInterval(() => void tick(), 3_000)
    return () => clearInterval(id)
  }, [state.upcomingSession, roomId, startElapsedTimer])

  // ── Active-session reconciliation poll ─────────────────────────────────────
  // Safety net for missed lifecycle events. Observed failure mode: the TV
  // sat on a PRESTART session ("waiting for coach") while a DIFFERENT
  // session went LIVE in the same room — the session:started event never
  // landed (connect race / buffer ordering), and none of the existing
  // self-recovery polls cover a session-identity change (they all poll
  // the session the TV already knows about).
  //
  // Rule: while the TV is NOT showing live action (ambient, PRESTART, or
  // ENDED), ask the server every 15s which session is actually active in
  // this room. If the server names a different session — or the same one
  // in a more advanced state — adopt it via the full snapshot fetch.
  // 15s is slow enough to be negligible load (4 req/min per screen) and
  // fast enough that a missed start costs at most one QR rotation of
  // confusion. Deliberately NOT run while LIVE/PAUSED: the socket owns
  // steady-state, and a poll racing a live handoff could fight the
  // handoff-split logic.
  useEffect(() => {
    if (!roomId) return
    const status = state.sessionStatus
    if (status === 'LIVE' || status === 'PAUSED') return

    const tick = async () => {
      try {
        const res = await screenApi.getActiveSession(roomId)
        const active = res.data as { id: string; status: string } | null
        if (!active?.id) return
        const cur = stateRef.current
        const identityChanged = active.id !== cur.sessionId
        const statusAdvanced =
          active.id === cur.sessionId &&
          (active.status === 'LIVE' || active.status === 'PAUSED') &&
          cur.sessionStatus === 'PRESTART'
        if (identityChanged || statusAdvanced) {
          console.info(
            '[TV] reconcile: server active session',
            active.id,
            active.status,
            '— local was',
            cur.sessionId,
            cur.sessionStatus,
          )
          await fetchSnapshot(active.id)
        }
      } catch {
        // Transient network error — next tick retries.
      }
    }
    const id = setInterval(() => void tick(), 15_000)
    return () => clearInterval(id)
  }, [roomId, state.sessionStatus, fetchSnapshot])

  // Loads the current active session for this room (PRESTART / LIVE / PAUSED).
  // Does NOT touch upcomingSession — call fetchNextUp separately after this.
  const fetchActiveAndSnapshot = useCallback(async () => {
    if (!roomId) return
    try {
      const res = await screenApi.getActiveSession(roomId)
      const active = res.data as { id: string; status: string } | null

      if (active?.id) {
        // ── Split-screen refresh recovery ──────────────────────────────────────
        // If the API returns a PRESTART session it means the previous (LIVE)
        // session has already ended and the Redis room-key was cleared.  Before
        // loading the PRESTART as the main session, check whether we have a
        // recently-ended session saved in localStorage (written in
        // session:ended handler below).  If so, restore the ENDED+PRESTART
        // split screen so athletes can still see the final leaderboard after
        // a browser refresh.
        if ((active.status as string) === 'PRESTART') {
          const saved = getLastEndedSession()
          // Align with handoff window in App.tsx so recovery and UI agree.
          if (saved && saved.roomId === roomId && Date.now() - saved.endedAt < REFRESH_RECOVERY_WINDOW_MS) {
            try {
              const endedSession = JSON.parse(saved.sessionJson)
              const endedLeaderboard = saved.leaderboardJson ? JSON.parse(saved.leaderboardJson) : null
              // Restore class A as the main ENDED session; fetchNextUp (called
              // next in the connect handler) will pick up class B as upcoming.
              setState((prev) => ({
                ...prev,
                sessionId: endedSession.id,
                session: endedSession,
                sessionStatus: 'ENDED' as SessionStatus,
                leaderboard: endedLeaderboard,
                elapsedMs: 0,
              }))
              console.info('[TV] refresh: restored ended session from localStorage for split screen')
              return // fetchNextUp will set class B as upcomingSession
            } catch {
              // Corrupt localStorage — fall through to normal load
            }
          }
        }

        await fetchSnapshot(active.id)
      } else {
        setState((prev) => ({ ...prev, session: null, sessionStatus: null, sessionId: null }))
      }
    } catch (err) {
      console.error('[useScreenSession] active session fetch failed', err)
    }
  }, [roomId, fetchSnapshot])


  // ─── Event dismissal helpers ──────────────────────────────────────────────────

  const setOvertake = useCallback((event: OvertakeEvent) => {
    // Cooldown gate — see OVERTAKE_COOLDOWN_MS. Drop (don't queue) events
    // that arrive while a recent takeover is still "fresh"; the in-list
    // animations carry routine rank churn.
    const now = Date.now()
    if (now - lastOvertakeShownAtRef.current < OVERTAKE_COOLDOWN_MS) return
    lastOvertakeShownAtRef.current = now

    if (overtakeTimerRef.current) clearTimeout(overtakeTimerRef.current)
    setState((prev) => ({ ...prev, overtakeEvent: event }))
    overtakeTimerRef.current = setTimeout(() => {
      setState((prev) => ({ ...prev, overtakeEvent: null }))
    }, EVENT_DISMISS_MS)
  }, [])

  const setComeback = useCallback((event: ComebackEvent) => {
    // Same storm-protection as setOvertake — comebacks fire per recalc
    // too, and a permanently-parked banner stops reading as an event.
    const now = Date.now()
    if (now - lastComebackShownAtRef.current < OVERTAKE_COOLDOWN_MS) return
    lastComebackShownAtRef.current = now

    if (comebackTimerRef.current) clearTimeout(comebackTimerRef.current)
    setState((prev) => ({ ...prev, comebackEvent: event }))
    comebackTimerRef.current = setTimeout(() => {
      setState((prev) => ({ ...prev, comebackEvent: null }))
    }, EVENT_DISMISS_MS)
  }, [])

  const setShoutout = useCallback((event: ShoutoutEvent) => {
    if (shoutoutTimerRef.current) clearTimeout(shoutoutTimerRef.current)
    setState((prev) => ({ ...prev, shoutoutEvent: event }))
    shoutoutTimerRef.current = setTimeout(() => {
      setState((prev) => ({ ...prev, shoutoutEvent: null }))
    }, EVENT_DISMISS_MS)
  }, [])

  // ─── Socket setup ─────────────────────────────────────────────────────────────

  // Track the last roomId we opened a socket for.  On change (re-pair), reset
  // all room-specific state so data from the old room can't leak onto the new
  // room's display (upcomingSession, previousSession, roster, leaderboard, QR).
  const lastRoomIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!roomId) return
    if (lastRoomIdRef.current !== null && lastRoomIdRef.current !== roomId) {
      console.info('[TV] roomId changed — resetting room-scoped state')
      // Clear lastEndedSession so the useState factory on a subsequent refresh
      // doesn't restore the old room's previousSession for the new room.
      clearLastEndedSession()
      setState(() => ({ ...DEFAULT_STATE }))
      stopElapsedTimer()
    }
    lastRoomIdRef.current = roomId
  }, [roomId, stopElapsedTimer])

  useEffect(() => {
    if (!roomId) return

    const token = getScreenToken()
    tenantIdRef.current = decodeTenantIdFromScreenToken(token)

    const socket = io(`${WS_URL}/ws`, {
      auth: { token },
      reconnection: false, // we handle reconnection manually
      // Allow polling as an upgrade path — pure-WS-only fails on first hiccup
      transports: ['polling', 'websocket'],
      upgrade: true,
    })

    socketRef.current = socket

    // ── Connect ──
    socket.on('connect', () => {
      console.info('[TV] socket connected', socket.id)
      // Reset the backoff counter so the next transient drop doesn't start
      // from the tail of the BACKOFF array (would otherwise wait 30s).
      reconnectAttemptRef.current = 0
      setState((prev) => ({ ...prev, isConnected: true, isReconnecting: false }))
      socket.emit('screen:join', { roomId })
      // Re-sync: load current session first, then check for upcoming.
      // Sequential so sessionId is set before fetchNextUp runs its guard.
      void (async () => {
        await fetchActiveAndSnapshot()
        await fetchNextUp()
      })()
      // Fetch the authoritative screen program in parallel — it drives the
      // phase decision; the session/leaderboard data above fills the panels.
      void fetchProgram()
    })

    // ── Disconnect ──
    socket.on('disconnect', (reason) => {
      console.warn('[TV] socket disconnected', reason)
      setState((prev) => ({ ...prev, isConnected: false, isReconnecting: true }))
      // Drop all broadcast subscriptions — the server will have lost the room
      // memberships, and the reconnect handler will re-join from scratch based
      // on the freshest sessionId / upcomingSession.id state.
      broadcastJoinedRef.current.clear()
      stopElapsedTimer()
      scheduleReconnect()
    })

    // ── Failed (re)connect ──
    // A reconnect attempt that fails (API mid-restart during a deploy, TV
    // booting while the server is down) fires connect_error, NOT disconnect
    // — the socket never connected. Without this handler the TV made one
    // attempt and then sat on "Reconnecting…" forever. Keep backing off.
    socket.on('connect_error', (err) => {
      console.warn('[TV] connect failed', err?.message ?? err)
      setState((prev) => ({ ...prev, isConnected: false, isReconnecting: true }))
      scheduleReconnect()
    })

    // Helper to unwrap the gateway envelope { event, data, roomId, ts }
    function unwrap<T>(envelope: { data?: T } & T): T {
      return (envelope.data ?? envelope) as T
    }

    // ── session:state (full sync on join) ──
    socket.on('session:state', (envelope) => {
      const payload = unwrap<SessionStatePayload>(envelope)
      const session = payload.session
      if (!session) return
      const baseMs = payload.elapsedMs ?? 0
      setState((prev) => ({
        ...prev,
        sessionId: session.id,
        session,
        sessionStatus: session.status as SessionStatus,
        leaderboard: payload.leaderboard,
        elapsedMs: baseMs,
      }))
      if ((session.status as string) === 'LIVE') {
        startElapsedTimer(baseMs)
      } else {
        stopElapsedTimer()
      }
      // Re-check for an upcoming session — we may have been offline while the
      // previous one was cancelled or a new one scheduled.
      void fetchNextUp()
    })

    // ── session:started ──
    socket.on('session:started', (envelope) => {
      const session = unwrap<LiveSession>(envelope)
      // NOTE: do NOT clear lastEndedSession here — when class B starts we still
      // need class A's frozen final leaderboard for the 5-minute handoff split.
      // The record expires naturally via the REFRESH_RECOVERY_WINDOW_MS time
      // gate, and each new session:ended overwrites it anyway.

      // Defensive: if we're replacing a different LIVE/PAUSED session without
      // having received its session:ended event (e.g., event loss on reconnect,
      // or the server's auto-end emitted but was missed), promote the outgoing
      // session to previous* and persist it so the handoff split still appears.
      const prevSnap = stateRef.current
      const outgoingDropped =
        prevSnap.sessionId &&
        prevSnap.sessionId !== session.id &&
        (prevSnap.sessionStatus === 'LIVE' || prevSnap.sessionStatus === 'PAUSED') &&
        prevSnap.session
      // Same cross-room guard as session:ended — don't persist an outgoing
      // session that doesn't belong to this TV's current room.
      if (outgoingDropped && roomId && prevSnap.session?.roomId === roomId) {
        console.info('[TV] session:started replacing a still-LIVE session — promoting to previous*')
        setLastEndedSession({
          sessionJson: JSON.stringify(prevSnap.session),
          leaderboardJson: prevSnap.leaderboard ? JSON.stringify(prevSnap.leaderboard) : null,
          endedAt: Date.now(),
          roomId,
        })
      }

      setState((prev) => {
        // Only clear the upcoming slots if THIS started session IS the upcoming
        // one being promoted to main.  Previously we cleared unconditionally,
        // which wiped class B's QR/roster the moment class A (a totally
        // different session) went LIVE — leaving the pre-class-B split panel
        // stuck on "Refreshing…" until the next QR rotation ~25 s later.
        const isPromotingUpcoming = prev.upcomingSession?.id === session.id
        return {
          ...prev,
          sessionId: session.id,
          session,
          sessionStatus: 'LIVE',
          ...(isPromotingUpcoming
            ? {
                upcomingSession: null,
                upcomingQrToken: null,
                upcomingQrSignature: null,
                upcomingQrExpiresAt: null,
                upcomingRoster: [],
              }
            : {}),
          // Promote the outgoing session into previous* for handoff split.
          ...(outgoingDropped
            ? {
                previousSession: prev.session,
                previousLeaderboard: prev.leaderboard,
                previousEndedAt: Date.now(),
              }
            : {}),
        }
      })
      // Start the timer from 0 — session just began
      startElapsedTimer(0)

      // Safety-net: the server emits leaderboard:update BEFORE session:started,
      // so the leaderboard should already be in state when we get here.
      // If for any reason it didn't arrive (network gap, empty roster at start),
      // fetch the snapshot after 1.5 s — only if leaderboard is still missing.
      const safetySessionId = session.id
      setTimeout(() => {
        if (stateRef.current.sessionId === safetySessionId &&
            stateRef.current.sessionStatus === 'LIVE' &&
            (stateRef.current.leaderboard === null || stateRef.current.leaderboard.entries.length === 0)) {
          console.info('[TV] leaderboard still empty 1.5 s after session:started — fetching snapshot')
          void fetchSnapshot(safetySessionId)
        }
      }, 1500)
    })

    // ── session:paused ──
    socket.on('session:paused', (envelope) => {
      const session = unwrap<LiveSession>(envelope)
      // Guard: ignore stale paused if it doesn't match our current session,
      // INCLUDING the case where we have no current session at all (sessionId
      // null).  A TV on AmbientScreen should never act on lifecycle events for
      // a session it never knew.
      if (stateRef.current.sessionId !== session.id) {
        console.info('[TV] ignoring session:paused for', session.id, '— current is', stateRef.current.sessionId)
        return
      }
      // Capture elapsed before stopping so the display freezes at the right value
      const frozenMs = sessionStartMsRef.current !== null
        ? Math.max(0, Date.now() - sessionStartMsRef.current)
        : stateRef.current.elapsedMs
      stopElapsedTimer()
      setState((prev) => ({
        ...prev,
        session,
        sessionStatus: 'PAUSED',
        elapsedMs: frozenMs,
      }))
    })

    // ── session:resumed ──
    socket.on('session:resumed', (envelope) => {
      const session = unwrap<LiveSession>(envelope)
      // Guard: reject when id doesn't match, including null (see session:paused).
      if (stateRef.current.sessionId !== session.id) {
        console.info('[TV] ignoring session:resumed for', session.id, '— current is', stateRef.current.sessionId)
        return
      }
      // Resume from wherever the timer was frozen at pause time.  Prefer the
      // authoritative sessionStartMsRef — elapsedMs is only updated once per
      // second by the interval, so could be up to ~1s stale.
      const resumeFrom = sessionStartMsRef.current !== null
        ? Math.max(0, Date.now() - sessionStartMsRef.current)
        : stateRef.current.elapsedMs
      setState((prev) => ({
        ...prev,
        session,
        sessionStatus: 'LIVE',
      }))
      startElapsedTimer(resumeFrom)
    })

    // ── session:ended ──
    socket.on('session:ended', (envelope) => {
      const session = unwrap<LiveSession>(envelope)

      // Guard: reject when id doesn't match our current sessionId, including
      // when sessionId is null.  Previously the `&&` short-circuit allowed
      // ended events for unrelated sessions to slip through on a freshly-
      // paired TV (sessionId still null), painting a phantom EndedScreen.
      if (stateRef.current.sessionId !== session.id) {
        console.info('[TV] ignoring session:ended for', session.id, '— current is', stateRef.current.sessionId)
        return
      }

      const finalMs = sessionStartMsRef.current !== null
        ? Math.max(0, Date.now() - sessionStartMsRef.current)
        : stateRef.current.elapsedMs
      stopElapsedTimer()

      // Snapshot the current session + leaderboard before we transition so
      // the 5-minute handoff split can display class A's final results while
      // class B's live leaderboard fills the right panel.
      const frozenSession = stateRef.current.session
      const frozenLeaderboard = stateRef.current.leaderboard

      // Persist to localStorage so a browser refresh during the split screen
      // (ENDED + upcoming PRESTART) can restore the final leaderboard on the
      // left panel without re-fetching from the server.
      //
      // Prefer the server's endedAt (from the updated session payload) over
      // the TV's local Date.now() — avoids drift if the TV clock is off due
      // to NTP skew.  Falls back to Date.now() if the server value is missing
      // or unparseable.
      const serverEndedAt = session.endedAt ? new Date(session.endedAt).getTime() : NaN
      const endedAtMs = Number.isFinite(serverEndedAt) ? serverEndedAt : Date.now()

      // Cross-check roomId: if the TV was re-paired mid-session, frozenSession
      // might belong to a DIFFERENT room than `roomId` — persisting under the
      // new roomId would resurrect an unrelated session on next refresh.
      if (frozenSession && roomId && frozenSession.roomId === roomId) {
        setLastEndedSession({
          sessionJson: JSON.stringify(frozenSession),
          leaderboardJson: frozenLeaderboard ? JSON.stringify(frozenLeaderboard) : null,
          endedAt: endedAtMs,
          roomId,
        })
      }

      setState((prev) => ({
        ...prev,
        // Merge rather than replace: the socket payload is the bare Prisma
        // row (no classInstance/template/coach includes), but the in-memory
        // session came from the snapshot fetch WITH those includes. A plain
        // replace loses the class name and the EndedScreen podium falls
        // back to the generic "Session" title. Fresh scalars (status,
        // endedAt, totalPausedMs) win; nested includes survive.
        session:
          prev.session && prev.session.id === session.id
            ? {
                ...prev.session,
                ...session,
                classInstance:
                  (session as any).classInstance ?? prev.session.classInstance,
              }
            : session,
        sessionStatus: 'ENDED',
        elapsedMs: finalMs,
        previousSession: frozenSession,
        previousLeaderboard: frozenLeaderboard,
        previousEndedAt: endedAtMs,
      }))
      // Check immediately for an upcoming session (for split screen)
      void fetchNextUp()
    })

    // ── session:upcoming — next PRESTART session created for this room ──
    socket.on('session:upcoming', (envelope) => {
      const upcoming = unwrap<LiveSession>(envelope)
      const { sessionStatus: currentStatus } = stateRef.current

      if (!currentStatus) {
        // Truly idle (ambient / never had a session): load upcoming as the main
        // PreClassScreen so athletes can see the QR and check in right away.
        void fetchSnapshot(upcoming.id)
      } else {
        // A session exists (PRESTART, LIVE, PAUSED, or just ENDED).
        // Store upcoming as upcomingSession so the split-screen cases in App.tsx
        // take over:
        //   • LIVE/PAUSED + upcoming within 10 min  → case 4a SplitScreen (isLive)
        //   • ENDED + upcoming                      → case 5  SplitScreen (final lb)
        // Never overwrite the main session with the upcoming one — that would
        // skip the split screen and jump straight to PreClassScreen.
        setState((prev) => {
          if (prev.sessionId === upcoming.id) return prev   // guard: don't set as upcoming if already main
          return { ...prev, upcomingSession: upcoming }
        })
        void fetchUpcomingSnapshot(upcoming.id)
      }
    })

    // ── leaderboard:update ──
    // The server tags the envelope with `sessionId` for room-emitted events
    // and the LeaderboardState payload itself also carries a `sessionId`.
    // ── screen:program — server-declared phase ──
    // The authoritative "which screen" signal, emitted on every session
    // lifecycle change. We also re-fetch at the program's revalidateAt for
    // the clock-based transitions the server can't push. applyProgram owns
    // the timer bookkeeping.
    socket.on('screen:program', (envelope) => {
      const program = unwrap<ScreenProgram>(envelope)
      if (program && program.phase && program.roomId === roomId) {
        applyProgram(program)
      }
    })

    // Route strictly: a stray update for the upcoming (or an unrelated) session
    // must not overwrite the frozen final board shown during ENDED+PRESTART
    // split screen.
    socket.on('leaderboard:update', (envelope) => {
      const leaderboard = unwrap<LeaderboardState>(envelope)
      const evtSessionId: string | undefined = envelope?.sessionId ?? leaderboard?.sessionId
      // Prefer the broadcast-delayed copy: if this envelope came in on the
      // `room:<roomId>` channel (it has a `roomId` tag) AND we're already
      // subscribed to the same session's broadcast room, the delayed copy is
      // en route — drop the real-time one so the TV stays at public-stream
      // parity. Envelopes from `broadcast:<sid>` have no `roomId`.
      if (
        envelope?.roomId &&
        evtSessionId &&
        broadcastJoinedRef.current.has(evtSessionId)
      ) {
        return
      }
      setState((prev) => {
        if (evtSessionId && prev.sessionId && evtSessionId !== prev.sessionId) {
          // Belongs to a different session (e.g., upcoming or stale) — drop.
          return prev
        }
        // Monotonicity guard — see isStaleLeaderboardFrame.
        if (isStaleLeaderboardFrame(prev.leaderboard, leaderboard)) return prev
        return { ...prev, leaderboard }
      })
    })

    // ── roster:update — athlete checked in via QR ──
    // The envelope includes a sessionId so we can route to the right roster.
    socket.on('roster:update', (envelope) => {
      const raw = envelope.data ?? envelope
      const roster: RosterEntry[] = Array.isArray(raw) ? raw : (raw.roster ?? [])
      const sessionId: string | undefined = raw.sessionId ?? envelope.sessionId
      setState((prev) => {
        // Route strictly by sessionId.  If the event references a session that
        // matches neither the main nor the upcoming one, drop it — an unkeyed
        // write to either bucket would corrupt state (e.g., show class B's
        // roster on class A's leaderboard).
        if (sessionId) {
          if (prev.upcomingSession && sessionId === prev.upcomingSession.id) {
            return { ...prev, upcomingRoster: roster }
          }
          if (prev.sessionId && sessionId === prev.sessionId) {
            return { ...prev, roster }
          }
          console.info('[TV] roster:update: dropping event for unknown session', sessionId)
          return prev
        }
        // Legacy unkeyed payload: assume main session.
        return { ...prev, roster }
      })
    })

    // ── qr:refresh ──
    // Gateway wraps payload: { event, data: QrRefreshEvent, roomId, ts }
    socket.on('qr:refresh', (envelope: { data?: QrRefreshEvent } & QrRefreshEvent) => {
      const qr: QrRefreshEvent = envelope.data ?? envelope
      setState((prev) => {
        // Route by sessionId.  Anything that is NOT the current main session
        // is treated as the upcoming QR — this correctly parks the first
        // qr:refresh for a freshly-created PRESTART class B even when
        // `session:upcoming` hasn't yet populated `prev.upcomingSession`
        // (the two events race on the wire).  Otherwise the QR event would
        // fall through the "main" branch, overwrite class A's QR, and
        // class B would land with nothing → "Refreshing…" until next rotation.
        if (qr.sessionId && prev.sessionId && qr.sessionId !== prev.sessionId) {
          return {
            ...prev,
            upcomingQrToken: qr.token,
            upcomingQrSignature: qr.signature,
            upcomingQrExpiresAt: qr.expiresAt,
          }
        }
        // Explicit match against upcomingSession, for the case where main is
        // null (idle room) and only an upcoming is loaded.
        if (prev.upcomingSession && qr.sessionId === prev.upcomingSession.id) {
          return {
            ...prev,
            upcomingQrToken: qr.token,
            upcomingQrSignature: qr.signature,
            upcomingQrExpiresAt: qr.expiresAt,
          }
        }
        return {
          ...prev,
          qrToken: qr.token,
          qrSignature: qr.signature,
          qrExpiresAt: qr.expiresAt,
        }
      })
    })

    // Reject events whose envelope sessionId doesn't match the current main
    // session — prevents overtake/comeback/shoutout toasts for the upcoming
    // (or a stale) session from surfacing on the LIVE display.
    function isForMainSession(envelope: { sessionId?: string }): boolean {
      const evtSessionId = envelope?.sessionId
      const currentId = stateRef.current.sessionId
      if (!evtSessionId) return true    // legacy unkeyed payload — accept
      if (!currentId) return true       // no main yet — accept
      return evtSessionId === currentId
    }

    /**
     * Drop real-time `room:<roomId>` duplicates of cosmetic events when we're
     * already subscribed to the session's `broadcast:<sid>` room. The delayed
     * copy is authoritative for the TV's public-display stream — accepting
     * both would fire every toast twice (once instantly, once at delay).
     */
    function isRealtimeDuplicate(envelope: { sessionId?: string; roomId?: string }): boolean {
      if (!envelope?.roomId) return false
      const sid = envelope.sessionId
      if (!sid) return false
      return broadcastJoinedRef.current.has(sid)
    }

    // ── rank:overtake ──
    socket.on('rank:overtake', (envelope) => {
      if (isRealtimeDuplicate(envelope)) return
      if (!isForMainSession(envelope)) return
      setOvertake(unwrap<OvertakeEvent>(envelope))
    })

    // ── comeback:detected ──
    socket.on('comeback:detected', (envelope) => {
      if (isRealtimeDuplicate(envelope)) return
      if (!isForMainSession(envelope)) return
      setComeback(unwrap<ComebackEvent>(envelope))
    })

    // ── timer:update ── (interval timer started/stopped by the coach)
    socket.on('timer:update', (envelope) => {
      if (!isForMainSession(envelope)) return
      const ev = unwrap<IntervalTimerEvent>(envelope)
      setState((prev) => ({
        ...prev,
        timer: ev.timer,
        clockOffsetMs: ev.serverNow - Date.now(),
      }))
    })

    // ── coach:shoutout ──
    socket.on('coach:shoutout', (envelope) => {
      if (!isForMainSession(envelope)) return
      setShoutout(unwrap<ShoutoutEvent>(envelope))
    })

    socket.on('error', (err: unknown) => {
      console.error('[TV] socket error', err)
      // The gateway rejects a revoked or expired screen token with this
      // message and disconnects; reconnecting would loop forever.
      if ((err as { message?: string } | null)?.message === 'Invalid or expired token') {
        forgetPairing()
      }
    })

    return () => {
      socket.off()
      socket.disconnect()
      stopElapsedTimer()
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
      if (overtakeTimerRef.current) clearTimeout(overtakeTimerRef.current)
      if (comebackTimerRef.current) clearTimeout(comebackTimerRef.current)
      if (shoutoutTimerRef.current) clearTimeout(shoutoutTimerRef.current)
      if (programRevalidateRef.current) clearTimeout(programRevalidateRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId])

  // ─── Broadcast subscription sync ─────────────────────────────────────────────
  //
  // The TV is a public display — athletes watching the screen see the same
  // cosmetic events (leaderboard, overtakes, comebacks) as the athlete app,
  // on the same wall-clock delay. That means the TV subscribes to
  // `broadcast:<sessionId>` for whichever sessions it cares about (main +
  // upcoming) and drops the real-time `room:<roomId>` duplicates of those
  // events — otherwise every leaderboard update would render twice (once
  // instantly, once delayed) and the two streams would disagree for
  // BROADCAST_DELAY_MS every time.
  //
  // Control events (session:started / paused / resumed / ended / upcoming)
  // and `qr:refresh` continue to flow real-time via `room:<roomId>` — those
  // are control-plane and must not be delayed regardless of the broadcast
  // setting, so we don't touch them here.
  useEffect(() => {
    if (!state.isConnected) return
    const socket = socketRef.current
    if (!socket) return
    const tenantId = tenantIdRef.current ?? ''

    // Targets are the sessionIds we should currently have a broadcast
    // subscription for. Both the main session (when not ENDED) and the
    // upcoming session (if any) qualify — they both render cosmetic events.
    const targets = new Set<string>()
    if (state.sessionId && state.sessionStatus && state.sessionStatus !== 'ENDED') {
      targets.add(state.sessionId)
    }
    if (state.upcomingSession?.id) {
      targets.add(state.upcomingSession.id)
    }

    // Join newly-needed sessions.
    for (const sid of targets) {
      if (!broadcastJoinedRef.current.has(sid)) {
        socket.emit('session:join:broadcast', { sessionId: sid, tenantId })
        broadcastJoinedRef.current.add(sid)
        console.info('[TV] joined broadcast:', sid)
      }
    }
    // Leave subscriptions we no longer need (e.g., previous session ENDED and
    // is no longer eligible; upcoming was promoted or cancelled).
    for (const sid of Array.from(broadcastJoinedRef.current)) {
      if (!targets.has(sid)) {
        socket.emit('session:leave', { sessionId: sid })
        broadcastJoinedRef.current.delete(sid)
        console.info('[TV] left broadcast:', sid)
      }
    }
  }, [state.isConnected, state.sessionId, state.sessionStatus, state.upcomingSession?.id])

  // ─── Manual reconnect with exponential backoff ────────────────────────────────

  const BACKOFF = [1000, 2000, 4000, 8000, 16000, 30000]
  const reconnectAttemptRef = useRef(0)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
    const step = Math.min(reconnectAttemptRef.current, BACKOFF.length - 1)
    const delay = BACKOFF[step]
    reconnectAttemptRef.current += 1
    reconnectTimerRef.current = setTimeout(() => {
      const socket = socketRef.current
      if (socket && !socket.connected) {
        console.info(`[TV] reconnect attempt ${reconnectAttemptRef.current}`)
        socket.connect()
      }
    }, delay)
  }, [])

  return state
}
